import { uniq, flatten } from 'lodash'

import { encodeText, decodeText } from '@aytm/app/stats/lib/util/helpers'
import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { convertCustomVariables } from '@aytm/app/stats/lib/query/variables'
import { heldBackVariables } from '@aytm/app/stats/lib/page/held-back-variables'
import { productCode, productsInDisplayOrder } from '@aytm/app/stats/page/question-views/ec/lib/product-code'
import {
  getQuestionIcon,
  questionPositionToId,
  removeLogicFromText,
  removeTagsFromText,
  processTextString
} from '@aytm/app/stats/page/components/lib/utils'

const getSurveyTags = survey =>
  (survey &&
    Object.hasOwn(survey, 'config') &&
    survey.config &&
    Object.hasOwn(survey.config, 'tags') &&
    survey.config.tags) ||
  {}

const getSentimentsForQuestion = (question, paramsTags = {}, tagsData = {}, survey, features) => {
  if (features.oe_sentiments_view.disabled) return []

  const id = (0 < question.position && `q${question.position}`) || question.id
  const tags = Object.assign({}, getSurveyTags(survey).sentiment || {}, paramsTags.sentiment || {})

  let sentiments = []

  if (Object.hasOwn(question, 'batches') && 0 < question.batches.length) {
    sentiments =
      'hm' == question.type
        ? uniq(
            flatten(
              Object.keys(tagsData.tagsStats || {})
                .filter(_id => {
                  const p = _id.split('.')
                  return id === p[0] && tags[_id] !== false && tags[`${p[0]}.${p[1]}`] !== false && tags[p[0]] !== false
                })
                .map(_id => (tagsData.tagsStats[_id].sentiment || []).map(tag => tag.tag))
            )
          )
        : question.batches.reduce((acc, cur) => {
            const _sentiments = uniq(
              flatten(
                Object.keys(tagsData.tagsStats || {})
                  .filter(_id => {
                    const p = _id.split('.')
                    return (
                      id === p[0] &&
                      `b${cur.position}` === p[1] &&
                      tags[_id] !== false &&
                      tags[`${p[0]}.${p[1]}`] !== false &&
                      tags[p[0]] !== false
                    )
                  })
                  .map(_id => (tagsData.tagsStats[_id].sentiment || []).map(tag => tag.tag))
              )
            )
            if (0 < _sentiments.length) acc[`b${cur.position}`] = _sentiments

            return acc
          }, {})
  } else {
    sentiments =
      Object.hasOwn(question, 'answerVariants') && 0 < question.answerVariants.length && 'va' !== question.type
        ? uniq(
            flatten(
              Object.keys(tagsData.tagsStats || {})
                .filter(_id => {
                  const p = _id.split('.')
                  return id === p[0] && tags[_id] !== false && tags[p[0]] !== false
                })
                .map(_id => (tagsData.tagsStats[_id].sentiment || []).map(tag => tag.tag))
            )
          )
        : uniq(
            flatten(
              Object.keys(tagsData.tagsStats || {})
                .filter(_id => id === _id && tags[_id] !== false)
                .map(_id => (tagsData.tagsStats[_id].sentiment || []).map(tag => tag.tag))
            )
          )
  }

  return JSON.parse(JSON.stringify(sentiments))
}

const getTagsForQuestion = (question, tagsData = {}, features) => {
  if (features.oe_tags_view.disabled) return []

  const id = (0 < question.position && `q${question.position}`) || question.id

  let tags = []

  if (Object.hasOwn(question, 'batches') && 0 < question.batches.length) {
    tags =
      'hm' === question.type
        ? uniq(
            flatten(
              Object.keys(tagsData.tagsStats || {})
                .filter(_id => id === _id.split('.')[0])
                .map(_id =>
                  Object.keys(tagsData.tagsStats[_id])
                    .reduce(
                      (acc, cur) =>
                        ['junk', 'sentiment'].includes(cur) ? acc : acc.concat(tagsData.tagsStats[_id][cur]),
                      []
                    )
                    .map(tag => tag.tag)
                )
            )
          )
        : question.batches.reduce((acc, cur) => {
            const _tags = uniq(
              flatten(
                Object.keys(tagsData.tagsStats || {})
                  .filter(_id => id === _id.split('.')[0])
                  .map(_id =>
                    Object.keys(tagsData.tagsStats[_id])
                      .reduce(
                        (acc, cur) =>
                          ['junk', 'sentiment'].includes(cur) ? acc : acc.concat(tagsData.tagsStats[_id][cur]),
                        []
                      )
                      .map(tag => tag.tag)
                  )
              )
            )
            if (0 < _tags.length) acc[`b${cur.position}`] = _tags

            return acc
          }, {})
  } else {
    tags =
      Object.hasOwn(question, 'answerVariants') && 0 < question.answerVariants.length
        ? uniq(
            flatten(
              Object.keys(tagsData.tagsStats || {})
                .filter(_id => id === _id.split('.')[0])
                .map(_id =>
                  Object.keys(tagsData.tagsStats[_id])
                    .reduce(
                      (acc, cur) =>
                        ['junk', 'sentiment'].includes(cur) ? acc : acc.concat(tagsData.tagsStats[_id][cur]),
                      []
                    )
                    .map(tag => tag.tag)
                )
            )
          )
        : uniq(
            flatten(
              Object.keys(tagsData.tagsStats || {})
                .filter(_id => id === _id)
                .map(_id =>
                  Object.keys(tagsData.tagsStats[_id])
                    .reduce(
                      (acc, cur) =>
                        ['junk', 'sentiment'].includes(cur) ? acc : acc.concat(tagsData.tagsStats[_id][cur]),
                      []
                    )
                    .map(tag => tag.tag)
                )
            )
          )
  }
  return JSON.parse(JSON.stringify(tags))
}

const initCXOptions = (cur, { lvl = null }) => {
  if (['dlc', 'dld', 'dlt'].includes(cur.type)) return []

  let question

  if (['dlv', 'dlx'].includes(cur.type)) {
    question = JSON.parse(JSON.stringify(cur))
    question.type = cur.variant
    question.variant = cur.type
  } else question = cur
  if (!question) return []

  const id = (0 < question.position && question.position) || question.id
  const value = ((0 < question.position && questionPositionToId(question.position)) || question.id).toUpperCase()

  let acc = Array.from(Array(question.levels), (_, i) => {
    const _key = value
    const _value = `R${i + 1}`

    const option = {
      id: `q-${id}-r-${i + 1}`,
      key: _key,
      subkey: _value,
      parentId: `q-${id}`,
      title: _value,
      options: 0 < i ? {} : [],
      type: 'dropdown',
      scrollable: false,
      lvl: lvl
    }

    if (0 < i) {
      option.parent = Array.from(Array(i), (_, j) => `R${j + 1}`)
    }
    if (i < question.levels - 1) {
      option.children = Array.from(Array(question.levels - i - 1), (_, j) => `R${j + i + 2}`)
    }

    return option
  })

  const initAnswerVariants = (answerVariants, key) => {
    if (!answerVariants) return

    for (const av of answerVariants) {
      if (!av?.text.length) continue

      if (0 < av.level) {
        acc[av.level].options[key] = []
        acc[av.level].options[key].push({
          value: av.text,
          label: av.text,
          search: decodeText(av.text),
          position: av.position
        })
      } else {
        acc[av.level].options.push({
          value: av.text,
          label: av.text,
          search: decodeText(av.text),
          position: av.position
        })
      }

      initAnswerVariants(av.answerVariants, (key != undefined && `${key}-${av.position}`) || String(av.position))
    }
  }
  initAnswerVariants(question.answerVariants)

  return acc
}

const initBatchesOptions = (survey, model, cur, sentiments, manuals, { scrollable = true, lvl = null }) => {
  if (['dlc', 'dld', 'dlt'].includes(cur.type)) return []

  let question

  if (['dlv', 'dlx'].includes(cur.type)) {
    question = JSON.parse(JSON.stringify(cur))
    question.type = cur.variant
    question.variant = cur.type
  } else question = cur
  if (!question) return []

  if (
    !['md', 'cjc', 'cjd', 'emd', 'ad'].includes(question.type) &&
    !question.batches &&
    !question.answerVariants &&
    !question.sections
  )
    return []

  let acc = []

  const id = (0 < question.position && question.position) || question.id
  const value = ((0 < question.position && questionPositionToId(question.position)) || question.id).toUpperCase()

  if (question.questionVariants) {
    if (!(question.questionVariants || []).length) return acc
    if (!(question.batches || []).length) return acc

    acc = question.questionVariants.flatMap(qv => {
      const _key = `${value}E${qv.position}`

      return [
        {
          id: `q-${id}-e-${qv.position}`,
          key: _key,
          parentId: `q-${id}`,
          title: processTextString(qv.text),
          disabled: true,
          type: 'none'
        },
        ...question.batches
          .map(b => {
            if (!(b.answerVariants || []).length) return null

            const _key = `${value}E${qv.position}SQ${b.position}`
            // const _value = `SQ${b.position}`

            return {
              id: `q-${id}-e-${qv.position}-b-${b.position}`,
              key: _key,
              parentId: `q-${id}`,
              title: processTextString(b.text),
              options: b.answerVariants
                .map(av => {
                  if (null === av) return null
                  return {
                    value: `A${av.position}`,
                    label: `A${av.position}: ${processTextString(av.text)}`,
                    search: decodeText(`A${av.position}: ${av.text}`)
                  }
                })
                .filter(v => null !== v),
              type: 'multistatemultiselect',
              scrollable: false,
              lvl: lvl
            }
          })
          .filter(v => null !== v)
      ]
    })
  } else if ('mn' === question.type && question.sections) {
    acc = (question.sections || [])
      .filter(section => 0 < (section.answerVariants || []).length)
      .map(section => {
        const _key = `${value}SQ${section.position}`
        const _value = `SQ${section.position}`

        return {
          id: `q-${id}-sq-${section.position}`,
          key: _key,
          parentId: `q-${id}`,
          title: `${_value}: ${processTextString(section.text)}`,
          options: (section.answerVariants || [])
            .sort((a, b) => a.position - b.position)
            .map(av => {
              return {
                value: `A${av.position}`,
                label: `A${av.position}: ${processTextString(av.text)}`,
                search: decodeText(`A${av.position}: ${av.text}`)
              }
            }),
          type: 'multistatemultiselect',
          scrollable: false,
          lvl: lvl
        }
      })
  } else if (question.batches) {
    if (!(question.batches || []).length) return acc

    if ('hm' == question.type) {
      const clusters = (survey.assets['hm.cluster'] || []).filter(c => c.qid == `q${question.visual_position}`)

      // 0 < question.clusters?.length ||
      if (0 < clusters.length) {
        acc = question.batches.flatMap(b => {
          const _key = `${value}SQ${b.position}`
          const _value = `SQ${b.position}`

          return {
            id: `q-${id}-b-${b.position}`,
            key: _key,
            parentId: `q-${id}`,
            title: _value,
            text: processTextString(b.text),
            options: clusters.map(c => {
              return {
                value: c.uid,
                label: c.title,
                search: decodeText(c.title)
              }
            }),
            type: 'multistatemultiselect',
            scrollable: false,
            lvl: lvl
          }
        })
      }
    } else if (['ps', 'psc', 'vk'].includes(question.type)) {
      acc = question.batches
        .flatMap(b => {
          if ('vk' == question.type && !(b.filter_values || []).length) return null
          if ('vk' != question.type && !(b.answerVariants || []).length) return null

          const _key =
            'vk' == question.type
              ? `${value}SQ${b.part - 1}A${parseInt(b.id.split('_')[1], 10)}`
              : `${value}SQ${b.position}`
          const _value =
            'vk' == question.type ? `SQ${b.part - 1}A${parseInt(b.id.split('_')[1], 10)}` : `SQ${b.position}`

          return {
            id: `q-${id}-b-${b.position}`,
            key: _key,
            parentId: `q-${id}`,
            title: _value,
            // a vk batch's text is the engine's price-point placeholder, not words a reader can use
            text: 'vk' == question.type ? '' : processTextString(b.text),
            options:
              'vk' == question.type
                ? (b.filter_values || []).map((fv, i) => {
                    return {
                      value: 4 == b.part ? i + 1 : 2 == b.part ? Math.round(100 * fv) : fv,
                      label:
                        (Object.hasOwn(b, 'filter_displayValues') && b.filter_displayValues[i]) ||
                        `${b.beforePrice || ''}${fv}${b.afterPrice || ''}`,
                      search: decodeText(
                        (Object.hasOwn(b, 'filter_displayValues') && b.filter_displayValues[i]) ||
                          `${b.beforePrice || ''}${fv}${b.afterPrice || ''}`
                      )
                    }
                  })
                : b.answerVariants
                    .map(av => {
                      if (!av || !av.text) return null
                      return {
                        value: av.text,
                        label:
                          (question.scale_tmpl && question.scale_tmpl.replace(question.degree, av.text)) || av.text,
                        search: decodeText(av.text)
                      }
                    })
                    .filter(v => null !== v),
            type: 'sliderselect',
            scrollable: !['psc', 'vk'].includes(cur.type) && 'charts' == model.mode && scrollable,
            lvl: lvl
          }
        })
        .filter(v => null !== v)
    } else {
      if (!(question.batches[0].answerVariants || []).length) return acc

      acc = question.batches.flatMap(b => {
        const _key = `${value}SQ${b.position}`
        const _value = `${['ro', 'aro'].includes(question.type) ? 'A' : 'SQ'}${b.position}`

        const data = []

        const option = {
          id: `q-${id}-b-${b.position}`,
          key: _key,
          parentId: `q-${id}`,
          title: _value,
          text: processTextString(b.text),
          options: b.answerVariants
            .map(av => {
              if (!av) return null
              return {
                value: `A${av.position}`,
                label: `${['ro', 'aro'].includes(question.type) ? '' : 'A' + av.position + ': '}${processTextString(
                  av.text
                )}`,
                search: decodeText(`A${av.position}: ${av.text}`)
              }
            })
            .filter(v => null !== v),
          type: 'multistatemultiselect',
          scrollable: !['vd'].includes(cur.type) && 'charts' == model.mode && scrollable,
          lvl: lvl
        }
        if ('dlv' == cur.type) option.uid = b.options.uid

        data.push(option)

        if (Array.isArray(sentiments[`b${b.position}`]) && 0 < sentiments[`b${b.position}`].length) {
          data.push({
            id: `q-${id}-b-${b.position}-sentiments`,
            key: `TAG.SENTIMENT.${_key}`,
            parentId: `q-${id}`,
            title: 'Sentiments',
            options: sentiments[`b${b.position}`].map(sentiment => {
              return {
                value: sentiment,
                label: sentiment
              }
            }),
            type: 'multiselect',
            lvl: 1
          })
        }
        if (Array.isArray(manuals[`b${b.position}`]) && 0 < manuals[`b${b.position}`].length) {
          const options = survey.codeBooks.reduce((_acc, _cur) => {
            const _tags = _cur.tags.map(tag => `${_cur.title}: ${tag}`)
            if (!manuals[`b${b.position}`].some(tag => _tags.includes(tag))) return _acc

            _acc.push({
              text: `<i class='icon-aytmfd-folder-closed' style='color: #${_cur.color};'></i><span>${_cur.title}</span>`,
              items: _tags.map(tag => {
                return {
                  value: tag,
                  label: tag.replace(`${_cur.title}: `, ''),
                  search: decodeText(tag, false)
                }
              })
            })
            return _acc
          }, [])

          if (0 < options.length) {
            data.push({
              id: `q-${id}-b-${b.position}-tags`,
              key: `TAG.${_key}`,
              parentId: `q-${id}`,
              title: 'Codebooks',
              options: options,
              type: 'multiselect',
              group: true,
              lvl: 1
            })
          }
        }
        return data
      })
    }
  }

  return acc
}

const initQuestionsOptions = (survey, model, features, questions, { scrollable = true, draggable = false }) => {
  const tags = Object.assign({}, getSurveyTags(survey), model.tags || {})
  const tagsData = model.tagsData

  return questions.reduce((acc, question) => {
    if (!question || (!question.position && !question.id)) return acc

    const questionId = ((0 < question.position && questionPositionToId(question.position)) || question.id).toUpperCase()
    if (!Object.hasOwn(survey.refsList || {}, questionId)) return acc

    const questionType = question.variant || question.type

    let options = []

    const sentiments =
      (!model.isReportXpert && getSentimentsForQuestion(question, tags, tagsData, survey, features)) || []
    const manuals = (!model.isReportXpert && getTagsForQuestion(question, tagsData, features)) || []

    if ('ec' == questionType) {
      // products answer to their own code, listed parent-then-variants; a row the numbering
      // excludes has no reference, so it cannot be filtered on
      options = productsInDisplayOrder(question.answerVariants)
        .map(av => {
          const code = productCode(av)
          if (!code) return null
          return {
            value: code,
            label: `${code}: ${processTextString(av.text)}`,
            search: decodeText(`${code}: ${av.text}`)
          }
        })
        .filter(v => null !== v)
    } else if (['rb', 'cb', 'cbp', 'cbn', 'oex', 'scx', 'hm', 'sh', 'sft', 'cn'].includes(questionType)) {
      options = (question.answerVariants || [])
        .map(av => {
          if (!av || ('dlv' == question.type && !av.options.uid)) return null
          const option = {
            value: `A${av.position}`,
            label: `A${av.position}: ${processTextString(av.text)}`,
            search: decodeText(`A${av.position}: ${av.text}`)
          }

          if ('dlv' == question.type) option.uid = av.options.uid
          return option
        })
        .filter(v => null !== v)
    } else if ('hm' == questionType) {
      const clusters = (survey.assets['hm.cluster'] || []).filter(c => c.qid == `q${question.visual_position}`)

      options = (question.batches || [])
        .map(b => {
          return {
            value: `SQ${b.position}`,
            label: `SQ${b.position}: ${processTextString(b.text)}`,
            search: decodeText(`SQ${b.position}: ${b.text}`)
          }
        })
        .concat(
          clusters.map(c => {
            return {
              value: c.uid,
              label: c.title,
              search: decodeText(c.title)
            }
          })
        )
    } else if ('mn' == questionType) {
      options = (question.sections || [])
        .map(section => {
          return {
            value: `SQ${section.position}`,
            label: `SQ${section.position}: ${processTextString(section.text)}`,
            search: decodeText(`SQ${section.position}: ${section.text}`)
          }
        })
        .concat(
          (question.answerVariants || []).map(av => {
            return {
              value: `A${av.position}`,
              label: `A${av.position}: ${processTextString(av.text)}`,
              search: decodeText(`A${av.position}: ${av.text}`)
            }
          })
        )
    }

    acc.push({
      id: `q-${(0 < question.position && question.position) || question.id}`,
      key: questionId,
      title: questionId,
      text: processTextString(question.text),
      icon: getQuestionIcon(question),
      options:
        (Array.isArray(sentiments) && 0 < sentiments.length) || (Array.isArray(manuals) && 0 < manuals.length)
          ? []
          : options,
      type: ['rb', 'cb', 'cbp', 'cbn', 'oex', 'scx', 'hm', 'sh', 'ec', 'sft', 'cn', 'mn'].includes(questionType)
        ? 'multistatemultiselect'
        : 'button',
      disabled:
        (Array.isArray(sentiments) && 0 < sentiments.length) ||
        (Array.isArray(manuals) && 0 < manuals.length) ||
        ['cx', 'mx', 'sr', 'se', 'ro', 'aro', 'mp', 'qa', 'bpm', 'ps', 'psc', 'vk'].includes(questionType),
      scrollable,
      draggable,
      lvl: 0
    })

    if (
      (Array.isArray(sentiments) && 0 < sentiments.length) ||
      (Array.isArray(manuals) && 0 < manuals.length && 0 < options.length)
    ) {
      acc.push({
        id: `q-${(0 < question.position && question.position) || question.id}`,
        key: questionId,
        parentId: `q-${(0 < question.position && question.position) || question.id}`,
        title: questionId,
        text: processTextString(question.text),
        icon: getQuestionIcon(question),
        options: options,
        type: ['rb', 'cb', 'cbp', 'cbn', 'oex', 'scx', 'hm', 'sh', 'ec', 'sft', 'cn', 'mn'].includes(questionType)
          ? 'multistatemultiselect'
          : 'button',
        lvl: 1
      })
    }

    if ('cx' == questionType)
      // eslint-disable-next-line no-param-reassign
      acc = acc.concat(
        initCXOptions(question, {
          lvl: 1
        })
      )

    if (['mx', 'sr', 'se', 'ro', 'aro', 'mp', 'qa', 'bpm', 'ps', 'psc', 'vk', 'hm', 'mn'].includes(questionType))
      // eslint-disable-next-line no-param-reassign
      acc = acc.concat(
        initBatchesOptions(survey, model, question, sentiments, manuals, {
          scrollable,
          lvl: 1
        })
      )

    if (Array.isArray(sentiments) && 0 < sentiments.length) {
      acc.push({
        id: `q-${(0 < question.position && question.position) || question.id}-sentiments`,
        key: `TAG.SENTIMENT.${questionId}`,
        parentId: `q-${(0 < question.position && question.position) || question.id}`,
        title: 'Sentiments',
        options: sentiments.map(sentiment => {
          return {
            value: sentiment,
            label: sentiment
          }
        }),
        type: 'multiselect',
        lvl: 1
      })
    }

    if (Array.isArray(manuals) && 0 < manuals.length) {
      options = survey.codeBooks.reduce((_acc, _cur) => {
        const _tags = _cur.tags.map(tag => `${_cur.title}: ${tag}`)
        if (!manuals.some(tag => _tags.includes(tag))) return _acc

        _acc.push({
          text: `<i class='icon-aytmfd-folder-closed' style='color: #${_cur.color};'></i><span>${_cur.title}</span>`,
          items: _tags.map(tag => {
            return {
              value: tag,
              label: tag.replace(`${_cur.title}: `, ''),
              search: decodeText(tag, false)
            }
          })
        })
        return _acc
      }, [])
      if (0 < options.length) {
        acc.push({
          id: `q-${(0 < question.position && question.position) || question.id}-tags`,
          key: `TAG.${questionId}`,
          parentId: `q-${(0 < question.position && question.position) || question.id}`,
          title: 'Codebooks',
          options: options,
          type: 'multiselect',
          group: true,
          lvl: 1
        })
      }
    }

    return acc
  }, [])
}

// Other Filters rows label by raw key; these two keys do not name what they filter.
const OTHERS_LABELS = { group: 'Panel ID', group_id: 'Group ID' }

const initVariablesOptions = (variables, labels = {}) =>
  variables.map(variable => {
    let values = Object.assign([], variable.filter.values || [])
    const title = Object.hasOwn(labels, variable.key) ? labels[variable.key] : variable.key

    if ('n' == variable.filter.type) {
      values = values.map(v => Number(v))
      values.sort((a, b) => Number(a) - Number(b))
    }
    if ('t' == variable.filter.type) {
      values = values.filter(v => 0 < String(v).trim().length)
    }

    const filter = variable.filter.long
      ? {
          key: variable.key,
          title,
          options: [
            {
              value: 'note',
              label: `This filter has ${variable.filter.amount} values. Please use advanced mode for this filter.`,
              disabled: true
            }
          ],
          type: 'dropdown',
          lvl: 0
        }
      : {
          key: variable.key,
          title,
          options: values
            .map((v, i) => {
              const _v = String(v)

              let label = removeTagsFromText(
                (Object.hasOwn(variable.filter, 'displayValues') && variable.filter.displayValues[i]) || _v
              )
              label = label.replace(/(&#(\d+);)/g, (_match, _capture, charCode) => String.fromCharCode(charCode))
              label = decodeText(label)
              label = label.length > 100 ? label.slice(0, 97) + '...' : label
              label = encodeText(label)

              const value = removeLogicFromText(_v)
              return value == _v ? { value: value, label: label } : null
            })
            .filter(v => null !== v),
          type: ('x' == variable.filter.type && 'multistatemultiselect') || 'multiselect',
          lvl: 0
        }

    if ('x' == variable.filter.type) filter.derived = values.map(v => `${variable.key}_${v}`)

    return filter
  })

export const renderFilters = (survey, model, report, features, isReportXpert) => {
  const others = heldBackVariables({ features, refsList: survey.refsList, isAdmin: model.isAdmin })

  let variables = Object.keys(survey.variables || {})
    .filter(key => !others.includes(key) && (survey.variables[key].long || 0 < survey.variables[key].values.length))
    .filter(key => 'o' != survey.variables[key].type && '___' != key.slice(0, 3))
    .filter(key => Object.hasOwn(survey.refsList || {}, key.toUpperCase()))
  variables.sort()
  variables = variables.reduce((acc, cur) => {
    acc.push({
      filter: survey.variables[cur],
      key: cur
    })
    return acc
  }, [])

  let pipingVariables = Object.keys(survey.pipingVariables || {})
    .filter(key => survey.pipingVariables[key].long || 0 < survey.pipingVariables[key].values.length)
    .filter(key => 'o' != survey.pipingVariables[key].type && '___' != key.slice(0, 3))
    .filter(key => Object.hasOwn(survey.refsList || {}, key.toUpperCase()))
  pipingVariables.sort()
  pipingVariables = pipingVariables.reduce((acc, cur) => {
    acc.push({
      filter: survey.pipingVariables[cur],
      key: cur
    })
    return acc
  }, [])

  const _customVariables = convertCustomVariables(survey)

  let customVariables = Object.keys(_customVariables)
    .filter(key => !_customVariables[key].x && (_customVariables[key].long || 0 < _customVariables[key].values.length))
    .filter(key => 'o' != _customVariables[key].type && '___' != key.slice(0, 3))
    .filter(key => Object.hasOwn(survey.refsList || {}, key.toUpperCase()))
  customVariables.sort()
  customVariables = customVariables.reduce((acc, cur) => {
    acc.push({
      filter: _customVariables[cur],
      key: cur
    })
    return acc
  }, [])

  let derivedVariables = Object.keys(_customVariables).filter(
    key => (_customVariables[key].long || 0 < _customVariables[key].values.length) && /conjoint_cluster\d+/.test(key)
  )
  derivedVariables.sort()
  derivedVariables = derivedVariables.reduce((acc, cur) => {
    acc.push({
      filter: _customVariables[cur],
      key: cur
    })
    return acc
  }, [])

  let othersVariables = []
  if (model.isAdmin) {
    othersVariables = others
      .filter(
        key =>
          Object.hasOwn(survey.variables, key) &&
          !survey.variables[key].x &&
          (true == survey.variables[key].long || 0 < survey.variables[key].values.length)
      )
      .filter(key => 'o' != survey.variables[key].type && '___' != key.slice(0, 3))
      .filter(key => Object.hasOwn(survey.refsList || {}, key.toUpperCase()))
    othersVariables.sort()
    othersVariables = othersVariables.reduce((acc, cur) => {
      acc.push({
        filter: survey.variables[cur],
        key: cur
      })
      return acc
    }, [])
  }

  const traitsOptions = {
    key: 'traits',
    title: 'Demographics',
    help: `
        Take advantage of the <b>included traits</b> by slicing and dicing your data. Each trait here becomes a filter, all you have to do is select your filter option and click apply.
        Any combination of filters can be enabled at the same time.  If you need a greater control over filtering conditions, such as using an "OR" operator,
        please switch to the <a href="https://helpcenter.aytm.com/hc/en-us/articles/360049242211-Filter-Data-Results-Advanced-Mode" target="_blank" rel="noopener noreferrer">advanced mode</a> and express your filters with the help of aytm Logic.
        <br/><br/>
        <a href="https://helpcenter.aytm.com/hc/en-us/articles/360049242651-Filtering-your-results" target="_blank" rel="noopener noreferrer">Learn more &raquo;</a>
      `,
    values: convertTraits(survey).reduce((acc, cur) => {
      if ('country' == cur.id && 'WRD' != survey.country.id) return acc
      if (cur.missing || !Object.hasOwn(survey.refsList || {}, cur.id.toUpperCase())) return acc

      acc.push({
        key: cur.id,
        // Trait title and value names are researcher-supplied and render as innerHTML in the
        // filter row; encode so hostile markup stays inert (mirrors the customVariables path). OQ-17.
        title: encodeText(cur.title),
        options: cur.values.map(value => {
          return {
            value: value.id,
            label: encodeText(value.name)
          }
        }),
        type: 'multiselect',
        lvl: 0
      })
      return acc
    }, []),
    advanced: false
  }

  const reg = new RegExp('^xq(\\d+)(?:[_](\\d+))?$', 'i')
  const xquestions = Object.hasOwn(survey.scripts || {}, 'questions')
    ? Object.values(report.current.virtual)
        .filter(q => reg.test(q.id))
        .reduce((acc, cur) => {
          const q = Object.values(survey.scripts.questions).find(
            q => q.id.startsWith(`${cur.id}_`) && q.blocks.some(b => 'chart' == b.type)
          )
          if (q) {
            const chart = q.blocks.find(b => 'chart' == b.type)
            acc.push(
              Object.assign({}, chart, {
                id: cur.id,
                key: q.id,
                text: cur.text,
                type: cur.type,
                title: q.text,
                variant: ('horizontal-bars' == chart.subtype && 'rb') || chart.subtype,
                position: 0
              })
            )
          }
          return acc
        }, [])
    : []

  const virtualQuestionsOptions = {
    key: 'virtualQuestions',
    title: 'Virtual Questions',
    help: `
        <p>Here you can filter by answers given by respondents to look at the subset of the data.</p><br/>
        <p>To include a specific answer, click on the checkbox once, to exclude an answer click a second time, and to clear the filter selection back to the default click a third time.</p><br/>
        <p>Select your filters and click on "Apply filters" at the top to apply your filters to the entire data set.</p><br/><p>If you need greater control over filtering conditions,
        switch to the advanced mode and express your filters with the help of aytm Logic.</p><br/>
        <p>Advanced Tip: if you aren&apos;t sure how your specific filter set will be expressed hover over the Basic / Advanced toggle at the top to see how it&apos;s expressed as aytm logic.</p><br/>
        <a href="https://helpcenter.aytm.com/hc/en-us/articles/360049242611-Filter-by-Answer-Choices" target="_blank" rel="noopener noreferrer">Learn more about filtering answers here &raquo;</a>
      `,
    values: initQuestionsOptions(
      survey,
      model,
      features,
      isReportXpert
        ? []
        : Object.values(report.current.virtual).map(q => ('dlx' == q.type ? xquestions.find(xq => xq.id == q.id) : q)),
      {
        scrollable: isReportXpert ? false : 'charts' == model.mode,
        draggable: false
      }
    ),
    advanced: false
  }

  const questionsOptions = {
    key: 'questions',
    title: 'Questions',
    help: `
        <p>Here you can filter by answers given by respondents to look at the subset of the data.</p><br/>
        <p>To include a specific answer, click on the checkbox once, to exclude an answer click a second time, and to clear the filter selection back to the default click a third time.</p><br/>
        <p>Select your filters and click on "Apply filters" at the top to apply your filters to the entire data set.</p><br/><p>If you need greater control over filtering conditions,
        switch to the advanced mode and express your filters with the help of aytm Logic.</p><br/>
        <p>Advanced Tip: if you aren&apos;t sure how your specific filter set will be expressed hover over the Basic / Advanced toggle at the top to see how it&apos;s expressed as aytm logic.</p><br/>
        <a href="https://helpcenter.aytm.com/hc/en-us/articles/360049242611-Filter-by-Answer-Choices" target="_blank" rel="noopener noreferrer">Learn more about filtering answers here &raquo;</a>
      `,
    values: initQuestionsOptions(
      survey,
      model,
      features,
      isReportXpert ? survey.questions : survey.questions.filter(q => report.current.schema.includes(`q${q.position}`)),
      {
        scrollable: isReportXpert ? false : 'charts' == model.mode,
        draggable: false
      }
    ),
    advanced: false
  }

  const variablesOptions = {
    key: 'variables',
    title: 'Variables',
    help: `
        Here you can filter by <b>variables</b> (if you used any) to look at the subset of the data. Any combination of filters can be enabled at the same time.
        If you need a greater control over filtering conditions, such as using an &quot;OR&quot; operator, please switch to the
        <a href="https://helpcenter.aytm.com/hc/en-us/articles/360049242211-Filter-Data-Results-Advanced-Mode" target="_blank" rel="noopener noreferrer">advanced mode</a> and express your filters with the help of aytm Logic.
        <br/><br/><a href="https://helpcenter.aytm.com/hc/en-us/articles/360049242651-Filtering-your-results" target="_blank" rel="noopener noreferrer">Learn more &raquo;</a>
      `,
    values: initVariablesOptions(variables.concat(pipingVariables)),
    advanced: false
  }

  const rejectedOptions = {
    key: 'completes',
    title: 'Show Rejected',
    help: 'Here you can include or exclude rejected responses in OE',
    values: [
      {
        key: 'filters_rejected',
        title: '',
        options: [
          { value: 'completes', label: 'Show Completes' },
          { value: 'rejected', label: 'Show Rejected' },
          { value: 'completes_rejected', label: 'Show Completes & Rejected' }
        ],
        type: 'radiobutton',
        lvl: 0
      }
    ]
  }

  const derivedVariablesOptions = {
    key: 'derivedVariables',
    title: 'Question Variables',
    help: 'Here you can filter by auto generated question <b>variables</b>',
    values: initVariablesOptions(derivedVariables),
    advanced: false
  }

  const customVariablesOptions = {
    key: 'customVariables',
    title: 'Uploaded Variables',
    help: `
        <p>You can add new custom variables to your survey after it has completed!</p><br/><p>Download, augment, and re-upload exported Excel with coded map files or upload your own CSV or XLSX file.</p><br/>
        <p>As long as your file contains the original column of Response IDs as downloaded, you can create new columns for as many new variables as you like.
        Label each one with unique alphanumeric column headers that start with <i><b>aytm:</b></i> (e.g., <i><b>aytm:my_variable</b></i>) and include no special characters other than underscore.
        If you need to update existing variables, keep the column header the same and we&rsquo;ll replace the response data with your new upload.</p><br/>
        <p>Learn more about <a href="https://helpcenter.aytm.com/hc/en-us/articles/360048760532-Custom-Variable-Upload" target="_blank" rel="noopener noreferrer">Custom Variable Uploads</a></p>
      `,
    values: initVariablesOptions(customVariables)
  }

  const removedQuestionsOptions = {
    key: 'excludedQuestions',
    title: 'Excluded Questions',
    values: []
  }
  if (!features.excluded_questions.disabled) {
    Object.assign(removedQuestionsOptions, {
      values: initQuestionsOptions(
        survey,
        model,
        features,
        isReportXpert ? [] : survey.questions.filter(q => !report.current.schema.includes(`q${q.position}`)),
        {
          scrollable: false,
          draggable: isReportXpert ? false : 'charts' == model.mode
        }
      )
    })
  }

  const otherOptions = {
    key: 'others',
    title: 'Other Filters',
    values: initVariablesOptions(othersVariables, OTHERS_LABELS),
    hidden: true,
    advanced: false
  }

  const filtersOptions = []
  if (0 < traitsOptions.values.length) filtersOptions.push(traitsOptions)
  if (0 < virtualQuestionsOptions.values.length) filtersOptions.push(virtualQuestionsOptions)
  if (0 < questionsOptions.values.length) filtersOptions.push(questionsOptions)
  if (0 < variablesOptions.values.length) filtersOptions.push(variablesOptions)
  if ('comments' == model.mode) filtersOptions.push(rejectedOptions)
  if (0 < derivedVariablesOptions.values.length) filtersOptions.push(derivedVariablesOptions)
  if (0 < customVariablesOptions.values.length || !features.data_lab_upload.hidden)
    filtersOptions.push(customVariablesOptions)
  if (0 < removedQuestionsOptions.values.length) filtersOptions.push(removedQuestionsOptions)
  if (0 < otherOptions.values.length) filtersOptions.push(otherOptions)

  return filtersOptions
}

export const renderSurveySchema = (survey, model, report, features) => {
  const tags = Object.assign({}, getSurveyTags(survey), model.tags || {})
  const questions = features.excluded_questions.disabled
    ? survey.questions.filter(q => report.current.schema.includes(questionPositionToId(q.position, survey)))
    : survey.questions

  const reg = new RegExp('^xq(\\d+)(?:[_](\\d+))?$', 'i')
  const xquestions = Object.hasOwn(survey.scripts || {}, 'questions')
    ? Object.values(report.current.virtual)
        .filter(q => reg.test(q.id))
        .reduce((acc, cur) => {
          const q = Object.values(survey.scripts.questions).find(
            q => q.id.startsWith(`${cur.id}_`) && q.blocks.some(b => 'chart' == b.type)
          )
          if (q) {
            const chart = q.blocks.find(b => 'chart' == b.type)
            acc.push(
              Object.assign({}, chart, {
                id: cur.id,
                key: q.id,
                text: cur.text,
                type: cur.type,
                title: q.text,
                variant: ('horizontal-bars' == chart.subtype && 'rb') || chart.subtype,
                position: 0
              })
            )
          }
          return acc
        }, [])
    : []

  const surveySchema = {
    assets: survey.assets,
    traits: convertTraits(survey).reduce((acc, cur) => {
      if ('country' == cur.id && 'WRD' != survey.country.id) {
        return acc
      }
      if (!Object.hasOwn(survey.refsList || {}, cur.id.toUpperCase())) {
        return acc
      }
      acc.push(cur)
      return acc
    }, []),
    geoTraits: Array.isArray(survey.traits)
      ? (survey.traits || [])
          .filter(
            t =>
              // a trait carries `families` only when it is geographic, and a throw here leaves the
              // question's answer stuck at loading with nothing on screen to say why
              'trait_var' == t.kind &&
              0 < (t.values?.length ?? 0) &&
              (t.families?.includes('geo') || t.families?.includes('country') || t.families?.includes('region'))
          )
          .map(t => t.id)
          .filter((v, i, a) => i == a.indexOf(v))
      : ['country', 'state', 'county', 'msa', 'region', 'zip'],
    questions,
    xquestions,
    variables: survey.variables,
    pipingVariables: survey.pipingVariables,
    customVariables: convertCustomVariables(survey),
    refsList: Object.assign(
      {
        CODEBOOK: 't',
        TAG: 't'
      },
      survey.refsList || {}
    ),
    codeBooks: survey.codeBooks,
    tags: questions.reduce((acc, cur) => {
      const id = ((0 < cur.position && questionPositionToId(cur.position)) || cur.id).toUpperCase()

      const manuals = getTagsForQuestion(cur, model.tagsData, features)
      if (Array.isArray(manuals) && 0 < manuals.length) {
        acc[id] = survey.codeBooks.reduce((acc, cur) => {
          const _tags = cur.tags.map(tag => `${cur.title}: ${tag}`)
          if (!manuals.some(tag => _tags.includes(tag))) {
            return acc
          }
          return acc.concat(_tags)
        }, [])
      } else {
        for (const k of Object.keys(manuals || {})) {
          acc[`${id}${k.replace('b', 'SQ')}`] = survey.codeBooks.reduce(function (acc, cur) {
            const _tags = cur.tags.map(tag => `${cur.title}: ${tag}`)
            if (!manuals[k].some(tag => _tags.includes(tag))) {
              return acc
            }
            return acc.concat(_tags)
          }, [])
        }
      }
      return acc
    }, {}),
    sentiments: questions.reduce((acc, cur) => {
      const id = ((0 < cur.position && questionPositionToId(cur.position)) || cur.id).toUpperCase()

      const sentiments = getSentimentsForQuestion(cur, tags, model.tagsData, survey, features)
      if (Array.isArray(sentiments) && 0 < sentiments.length) {
        acc[id] = sentiments
      } else {
        for (const k of Object.keys(sentiments || {})) {
          acc[`${id}${k.replace('b', 'SQ')}`] = sentiments[k]
        }
      }
      return acc
    }, {})
  }

  const virtual = Object.keys(report.current.virtual).reduce((acc, cur) => {
    const q = {
      id: report.current.virtual[cur].id,
      type: report.current.virtual[cur].type,
      variant: report.current.virtual[cur].variant
    }
    if (Object.hasOwn(report.current.virtual[cur], 'answerVariants')) {
      q.answerVariants = report.current.virtual[cur].answerVariants.map(av => {
        return {
          position: av.position
        }
      })
    }
    acc[cur] = q
    return acc
  }, {})

  return Object.assign(surveySchema, { virtual })
}
